/**
 * Explore one indicator's published values: areas down the side, years or
 * breakdown categories across. Totals are the published values for the
 * larger area, never sums of the cells (adding up ratios gives nonsense).
 */
import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  Catalogue, ResultRow, ThresholdStatus, errorMessage, indicatorRegistryApi,
} from '../../../services/indicatorRegistryApi';
import { formatValue } from './ResultsTable';

const STATUS_BG: Record<ThresholdStatus, string> = { good: '#E6F4EA', watch: '#FEF3E0', poor: '#FCE8E6' };
const LAST_YEAR = new Date().getFullYear() - 1;
const YEARS = Array.from({ length: 6 }, (_, i) => LAST_YEAR + 1 - i);

interface Props { catalogue: Catalogue }

const IndicatorExplorer: React.FC<Props> = ({ catalogue }) => {
  const [indicatorId, setIndicatorId] = useState<number | ''>('');
  const [compare, setCompare] = useState<'year' | string>('year');
  const [geoLevel, setGeoLevel] = useState('district');
  const [year, setYear] = useState(LAST_YEAR);

  const { data: indicators = [] } = useQuery(
    ['indicator-registry', 'list', 'approved'],
    () => indicatorRegistryApi.list({ status: 'approved' }),
  );
  const indicator = indicators.find(i => i.id === indicatorId);
  const breakdown = compare === 'year' ? '' : compare;

  const params = { geo_level: geoLevel, breakdown, ...(breakdown ? { year } : {}) };
  const results = useQuery(
    ['indicator-registry', indicatorId, 'results', params],
    () => indicatorRegistryApi.results(indicatorId as number, params),
    { enabled: !!indicator },
  );
  // The row above the table: national values, broken down the same way.
  const nationalParams = { ...params, geo_level: 'national' };
  const national = useQuery(
    ['indicator-registry', indicatorId, 'results', nationalParams],
    () => indicatorRegistryApi.results(indicatorId as number, nationalParams),
    { enabled: !!indicator },
  );
  // "All" column when comparing breakdown categories: the published totals.
  const totalsParams = { breakdown: '', year };
  const totals = useQuery(
    ['indicator-registry', indicatorId, 'results', 'totals', geoLevel, year],
    async () => [
      ...await indicatorRegistryApi.results(indicatorId as number, { ...totalsParams, geo_level: 'national' }),
      ...await indicatorRegistryApi.results(indicatorId as number, { ...totalsParams, geo_level: geoLevel }),
    ],
    { enabled: !!indicator && !!breakdown },
  );

  const dim = catalogue.dimensions.find(d => d.code === breakdown);

  const table = useMemo(() => {
    const rows: ResultRow[] = [...(national.data || []), ...(results.data || [])];
    const columnKey = (r: ResultRow) => (breakdown ? r.breakdown_value : String(r.year));
    const columns = Array.from(new Set(rows.map(columnKey))).sort();
    const areas: { code: string; name: string; national: boolean }[] = [];
    const cells: Record<string, Record<string, ResultRow>> = {};
    for (const r of rows) {
      if (!cells[r.area_code]) {
        cells[r.area_code] = {};
        areas.push({ code: r.area_code, name: r.area_name, national: r.geo_level === 'national' });
      }
      cells[r.area_code][columnKey(r)] = r;
    }
    const totalByArea: Record<string, ResultRow> = {};
    for (const r of totals.data || []) totalByArea[r.area_code] = r;
    return { columns, areas, cells, totalByArea };
  }, [results.data, national.data, totals.data, breakdown]);

  const columnLabel = (c: string) =>
    breakdown ? dim?.values.find(v => v.code === c)?.label || 'Not stated' : c;

  const downloadCsv = () => {
    if (!indicator) return;
    const header = ['Area', ...table.columns.map(columnLabel), ...(breakdown ? ['All'] : [])];
    const lines = [header, ...table.areas.map(a => [
      a.name,
      ...table.columns.map(c => table.cells[a.code][c]?.value ?? ''),
      ...(breakdown ? [table.totalByArea[a.code]?.value ?? ''] : []),
    ])];
    const csv = lines.map(l => l.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${indicator.code}_${geoLevel}${breakdown ? `_${breakdown}_${year}` : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loading = results.isFetching || national.isFetching || totals.isFetching;
  const show = (r?: ResultRow) => (indicator ? formatValue(r?.value ?? null, indicator.unit, indicator.decimals) : '');

  return (
    <Box>
      <Typography variant="h6">Explore</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Compare one indicator across areas and years, or across its breakdowns.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Indicator</InputLabel>
          <Select
            label="Indicator" value={indicatorId}
            onChange={e => { setIndicatorId(e.target.value as number); setCompare('year'); }}
          >
            {indicators.map(i => <MenuItem key={i.id} value={i.id}>{i.code} · {i.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Level</InputLabel>
          <Select label="Level" value={geoLevel} onChange={e => setGeoLevel(e.target.value)}>
            <MenuItem value="region">Region</MenuItem>
            <MenuItem value="district">District</MenuItem>
            <MenuItem value="school">School</MenuItem>
          </Select>
        </FormControl>
        {indicator && (
          <ToggleButtonGroup size="small" exclusive value={compare} onChange={(_, v) => v && setCompare(v)}>
            <ToggleButton value="year">By year</ToggleButton>
            {indicator.breakdowns.map(b => (
              <ToggleButton key={b} value={b}>By {catalogue.dimensions.find(d => d.code === b)?.label.toLowerCase()}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        {breakdown && (
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel>Year</InputLabel>
            <Select label="Year" value={year} onChange={e => setYear(Number(e.target.value))}>
              {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        {loading && <CircularProgress size={20} />}
        <Box sx={{ flex: 1 }} />
        {indicator && table.areas.length > 0 && (
          <Button size="small" startIcon={<Download />} onClick={downloadCsv}>CSV</Button>
        )}
      </Stack>

      {!indicator && <Alert severity="info">Pick an approved indicator.</Alert>}
      {results.isError && <Alert severity="error">{errorMessage(results.error)}</Alert>}
      {indicator && !loading && !table.areas.length && (
        <Alert severity="info">No published values for this selection.</Alert>
      )}

      {indicator && table.areas.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 620 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 180 }}>Area</TableCell>
                {table.columns.map(c => <TableCell key={c} align="right">{columnLabel(c)}</TableCell>)}
                {breakdown && <TableCell align="right" sx={{ fontWeight: 700 }}>All</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {table.areas.map(a => (
                <TableRow key={a.code} hover>
                  <TableCell sx={{ fontWeight: a.national ? 700 : 400 }}>{a.name}</TableCell>
                  {table.columns.map(c => {
                    const r = table.cells[a.code][c];
                    return (
                      <TableCell
                        key={c} align="right"
                        title={r ? `${r.schools.toLocaleString()} schools` : undefined}
                        sx={{
                          fontVariantNumeric: 'tabular-nums', fontWeight: a.national ? 700 : 400,
                          bgcolor: r?.status ? STATUS_BG[r.status] : undefined,
                        }}
                      >
                        {show(r)}
                      </TableCell>
                    );
                  })}
                  {breakdown && (
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                      {show(table.totalByArea[a.code])}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default IndicatorExplorer;
